import React, { useState, useEffect } from 'react';
import { Shield, X, AlertTriangle, Info, Download, CheckCircle, Loader2 } from 'lucide-react';
import securityApi from '../../api/securityApi';
import toast from 'react-hot-toast';

const ExportConfirmationModal = ({ projectId, onClose }) => {
  const [warnings, setWarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [criticalAcknowledged, setCriticalAcknowledged] = useState(false);
  const [error, setError] = useState(null);

  // Fetch warnings on mount
  useEffect(() => {
    const fetchWarnings = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await securityApi.getExportWarnings(projectId);
        setWarnings(response);
      } catch (err) {
        console.error('Error fetching export warnings:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load warnings');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchWarnings();
    }
  }, [projectId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await securityApi.downloadExportPackage(projectId);

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/zip' });

      // Create temporary URL and trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-export-${projectId}-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Audit package downloaded successfully');

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Error downloading export package:', err);
      toast.error(err.response?.data?.detail || 'Failed to download package');
    } finally {
      setDownloading(false);
    }
  };

  const hasNoCriticalWarnings = !warnings?.critical || warnings.critical.length === 0;
  const canDownload = !loading && !downloading && (hasNoCriticalWarnings || criticalAcknowledged);

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-lg mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-600 rounded-lg">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Export Audit Package</h2>
          </div>
          <button
            onClick={onClose}
            disabled={downloading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
              <p className="text-gray-600">Checking export readiness...</p>
            </div>
          ) : error ? (
            // Error state
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800 mb-1">Error</h3>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 text-sm text-red-700 font-medium hover:text-red-800"
              >
                Retry
              </button>
            </div>
          ) : (
            // Warnings content
            <div className="space-y-4">
              {/* Critical Issues */}
              {warnings?.critical && warnings.critical.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <h3 className="text-sm font-semibold text-red-800">Critical Issues</h3>
                  </div>
                  <ul className="space-y-2 mb-4">
                    {warnings.critical.map((warning, index) => (
                      <li key={index} className="text-sm text-red-700 flex items-start gap-2">
                        <span className="flex-shrink-0 mt-1">•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={criticalAcknowledged}
                      onChange={(e) => setCriticalAcknowledged(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-red-600 rounded border-red-300 focus:ring-red-500"
                    />
                    <span className="text-sm text-red-800">
                      I understand these issues and want to export anyway
                    </span>
                  </label>
                </div>
              )}

              {/* Warnings */}
              {warnings?.warnings && warnings.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <h3 className="text-sm font-semibold text-amber-800">Warnings</h3>
                  </div>
                  <ul className="space-y-2">
                    {warnings.warnings.map((warning, index) => (
                      <li key={index} className="text-sm text-amber-700 flex items-start gap-2">
                        <span className="flex-shrink-0 mt-1">•</span>
                        <span>{warning}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Info */}
              {warnings?.info && warnings.info.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-5 h-5 text-blue-600" />
                    <h3 className="text-sm font-semibold text-blue-800">Information</h3>
                  </div>
                  <ul className="space-y-2">
                    {warnings.info.map((info, index) => (
                      <li key={index} className="text-sm text-blue-700 flex items-start gap-2">
                        <span className="flex-shrink-0 mt-1">•</span>
                        <span>{info}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Success state - no warnings at all */}
              {(!warnings?.critical || warnings.critical.length === 0) &&
               (!warnings?.warnings || warnings.warnings.length === 0) &&
               (!warnings?.info || warnings.info.length === 0) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <h3 className="text-sm font-semibold text-green-800 mb-1">All Clear</h3>
                      <p className="text-sm text-green-700">
                        No issues found. Your export package is ready.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            disabled={downloading}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            Go Back and Fix
          </button>
          <button
            onClick={handleDownload}
            disabled={!canDownload}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download Package
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportConfirmationModal;
